import * as Sentry from "@sentry/react";
import logger from "../../utils/logger";
import catalog from "./voice-tool-catalog.json";
import { taskTools } from "./task";
import { taskActionTools } from "./task-actions";
import { leaveTools } from "./leave";
import { triageTools } from "./triage";
import { agentTools } from "./agent";

const allTools = [...taskTools, ...taskActionTools, ...leaveTools, ...triageTools, ...agentTools];

function runtimeName(key) {
    return key.replace(/[^a-zA-Z0-9_]/g, "_");
}

const registry = new Map();
for (const tool of allTools) {
    registry.set(tool.key, {
        ...tool,
        runtimeName: tool.runtimeName || runtimeName(tool.key),
        schema: {
            ...tool.schema,
            name: tool.runtimeName || runtimeName(tool.key)
        }
    });
}

// Backward-compatible aliases for older unit tests and any stale sessions.
const aliases = {
    set_field: "task.set_field",
    lookup_person: "task.lookup_person",
    lookup_project: "task.lookup_project",
    clear_field: "task.clear_field",
    read_back_draft: "task.read_back_draft",
    commit_task: "task.commit",
    cancel: "task.cancel"
};

export function getRuntimeToolName(key) {
    return registry.get(key)?.runtimeName || runtimeName(key);
}

export function getToolsForAgent(agentConfig = {}) {
    const allowed = new Set(agentConfig.tool_keys || []);
    return Array.from(allowed)
        .map((key) => registry.get(key))
        .filter(Boolean);
}

export function getToolSchemasForAgent(agentConfig = {}) {
    return getToolsForAgent(agentConfig).map((tool) => tool.schema);
}

export function getToolCatalog() {
    return catalog;
}

export function getRegisteredToolKeys() {
    return Array.from(registry.keys()).sort();
}

export function hasTaskTools(agentConfig = {}) {
    return (agentConfig.tool_keys || []).some((key) => key.startsWith("task."));
}

export function hasTriageTools(agentConfig = {}) {
    return (agentConfig.tool_keys || []).some((key) => key.startsWith("task_triage."));
}

export async function executeRegisteredTool(name, args, ctx) {
    const byRuntime = allTools.find((tool) => (tool.runtimeName || runtimeName(tool.key)) === name);
    const key = byRuntime?.key || aliases[name] || name;
    const tool = registry.get(key);

    logger.info?.(`[voice-tool] -> ${key}`, args);
    Sentry.addBreadcrumb({
        category: "voice-tool",
        message: key,
        level: "info",
        data: { args, agentId: ctx?.agentConfig?.code }
    });

    if (!tool) {
        return { ok: false, detail: `unknown tool: ${name}` };
    }
    if (!ctx?.agentConfig?.tool_keys?.includes(key)) {
        return { ok: false, detail: `tool is not enabled for this agent: ${key}` };
    }

    try {
        const result = await tool.executor(args || {}, ctx);
        logger.info?.(`[voice-tool] <- ${key}`, result);
        return result;
    } catch (err) {
        logger.error(`[voice-tool] ${key} threw`, err);
        Sentry.captureException(err, {
            tags: { feature: "voice-agent", tool: key, agent: ctx?.agentConfig?.code }
        });
        return { ok: false, detail: err?.message || "tool execution failed" };
    }
}

export function buildToolInstructions(agentConfig = {}) {
    const tools = getToolsForAgent(agentConfig);
    const lines = tools.map((tool) => `- ${tool.schema.name}: ${tool.schema.description}`);
    const handoffs = agentConfig.handoff_target_codes || [];
    if (handoffs.length > 0) {
        lines.push(`Allowed handoff targets: ${handoffs.join(", ")}`);
    }
    return lines.length > 0 ? `\n\nAvailable tools:\n${lines.join("\n")}` : "";
}
